import React, { useEffect, useState, useContext, useMemo, useTransition, useCallback } from 'react'
import { Delete, Edit, AddToPhotos, ListAltOutlined } from '@mui/icons-material'
import { CircularProgress, TableCell, TableRow, Checkbox, Tooltip, Fab } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import _ from 'lodash'
import { Component as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { TableCellTooltip, Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { fieldSetContext, modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { getCellContent } from 'functions'
import { CvsTable } from '../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../components/table/enhanced-table-toolbar'
import * as SnackbarActions from '../../global/snackbar/snackbar.actions'
import { Fuzzy } from '../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const Fieldsets = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const ModalContext = useContext(modalContext)
  const [loading, setLoading] = useState(true)
  const { byId, fetchAllFieldSetsAndFields, updateFieldsets } = useContext(fieldSetContext)
  const memoizedRows = useMemo(() => Object.keys(byId).map(key => byId[key]), [byId])
  const [selectedIds, setSelectedIds] = useState([])
  const [deleteIsDisabled, setDeleteIsDisabled] = useState()
  const [searchTerm, setSearchTerm] = useState('')
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const [isPending, startTransition] = useTransition()

  const history = useHistory()
  const [selected, setSelected] = useState({})
  const columns = [
    { label: t`Name`, width: '40%', lookup: 'name' },
    { label: t`Updated Date`, type: 'date', width: '20%', lookup: 'updated_at' },
    { label: t`Description`, width: '40%', lookup: 'description' },
  ]

  const [displayFieldSets, setDisplayFieldSets] = useState(memoizedRows)

  useEffect(() => {
    setDisplayFieldSets(memoizedRows)
  }, [memoizedRows])

  useEffect(() => {
    setLoading(true)
    fetchAllFieldSetsAndFields().then(() => {
      setLoading(false)
    })
  }, [fetchAllFieldSetsAndFields])

  useEffect(() => {
    setSelectedIds(Object.keys(_.pickBy(selected, v => v)))
  }, [selected])

  useEffect(() => {
    setDeleteIsDisabled(systemGeneratedFieldsetSelected())
  }, [selectedIds])

  const systemGeneratedFieldsetSelected = () => {
    return (
      memoizedRows.filter(e =>
        selectedIds.some(f => {
          return (
            +f === +e.id &&
            e.description ===
              t`THIS IS A SYSTEM-GENERATED FIELDSET. PLEASE DO NOT MANUALLY MAKE ANY CHANGES.`
          )
        }),
      ).length > 0
    )
  }

  const filterChanged = search => {
    setSearchTerm(search)

    startTransition(() => {
      const results = performSearch(search)
      setDisplayFieldSets(results)
    })
  }

  const fuzzy = useMemo(() => new Fuzzy(memoizedRows, { keys: ['name'] }), [memoizedRows])

  const performSearch = useCallback(
    term => {
      if (!term || term.trim() === '') {
        return memoizedRows
      }

      const results = fuzzy.search(term)

      return results
    },
    [fuzzy, memoizedRows],
  )

  const duplicateItem = () => {
    const edit = Object.keys(selected)[0]
    history.push(`/admin/fieldsets/new/fields?duplicate=${edit}`)
  }

  const editItems = () => {
    const edit = Object.keys(selected)[0]
    history.push(`/admin/fieldsets/${edit}/fields`)
  }

  const addItem = () => {
    history.push(`/admin/fieldsets/new/fields`)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/fieldsets/bulk/delete', {
        fieldset_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredFieldsets = memoizedRows.filter(f => !payload[f.id])
        updateFieldsets(filteredFieldsets)
        setSelected({})
        setSearchTerm('')
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Fieldset deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(e => {
        const message = e?.response?.data?.message?.includes('to power a List')
          ? e.data.message
          : t`Fieldset failed to delete`
        dispatch(
          SnackbarActions.showSnackbar({
            message,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  let description = t`Field Sets are an integral part of the Claravine experience and templates may depend on them. Please be sure this is what you'd like to do as this action can't be undone.`
  if (DATASETS_ENABLED) {
    description = t`Field Sets are an integral part of the Claravine experience and datasets may depend on them. Please be sure this is what you'd like to do as this action can't be undone.`
  }
  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these Field Sets?`,
      description,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  const deleteTooltipMessage = deleteIsDisabled
    ? systemGeneratedFieldsetSelected()
      ? t`System generated fieldsets cannot be deleted.`
      : t`This fieldset contains a field that is being used in a template that powers a list. To delete, please first unpower the associated list.`
    : t`Delete`

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
      data-test-page-id="admin-fieldsets"
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Field sets`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`A collection of columns that can be used within a submission`}{' '}
            {DATASETS_ENABLED ? 'dataset' : 'template'}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {!loading ? (
          <div
            className={classes.table}
            style={{ height: 'calc(100% - 85px)' }}
            data-test-cmp-id="admin-fieldsets-container"
          >
            <EnhancedTableToolbar
              numSelected={numSelected}
              filterChanged={filterChanged}
              term={searchTerm}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              blockMultipleEdit={false}
              duplicateItem={duplicateItem}
            >
              <Tooltip title={t`Duplicate`}>
                <Fab
                  name="duplicate"
                  style={{
                    color: 'white',
                    marginRight: '10px',
                  }}
                  color="secondary"
                  size="small"
                  aria-label="Duplicate"
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={duplicateItem}
                >
                  <AddToPhotos />
                </Fab>
              </Tooltip>
              <Tooltip title={t`Edit`}>
                <Fab
                  name="edit"
                  color="primary"
                  style={{ color: 'white', marginRight: '10px' }}
                  size="small"
                  aria-label="Edit"
                  className={classes.fab}
                  disabled={numSelected > 1}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip title={deleteTooltipMessage}>
                <span>
                  <Fab
                    name="delete"
                    style={{
                      backgroundColor: deleteIsDisabled ? 'rgba(0, 0, 0, 0.12)' : colors.red.light,
                      color: 'white',
                    }}
                    size="small"
                    aria-label="Delete"
                    className={classes.fab}
                    disabled={deleteIsDisabled}
                    onClick={deleteItemsModal}
                  >
                    <Delete />
                  </Fab>
                </span>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                isLoading={isPending}
                rows={displayFieldSets}
                orderBy="updated_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                multiSelect={true}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <ListAltOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Fieldsets Found.`,
                  description: t`Try a different search or add a new Fieldset.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayFieldSets.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        numSelected
                          ? setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          : history.push(`/admin/fieldsets/${row.id}/fields`)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.updated_at)}
                      </TableCell>
                      <TableCellTooltip title={row.description}>
                        <TableCell
                          component="th"
                          scope="row"
                          padding="normal"
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.description}
                        </TableCell>
                      </TableCellTooltip>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
